import useForgeRock from "hooks/useForgeRock";
import { useCallback, useEffect, useState } from "react";
import { getPowerBiEmbedUrl } from "services/apis";
import { reportResponse } from "types/ReportingAPI/ReportResponse";

export type embeddedReport = {
  loading: boolean;
  error: boolean;
  errorMessage?: string;
  id: string;
  embedUrl: string;
  embedToken: string;
  pageId: string;
};

const updateReportData = (
  reports: embeddedReport[],
  newReport: Partial<embeddedReport> & { id: string }
): embeddedReport[] => {
  return reports.map((report) =>
    report.id === newReport.id
      ? {
        ...report,
        ...newReport,
      }
      : report
  );
};

const useGetEmbeddedURLs = (reportIds: string[]) => {
  const { APICall } = useForgeRock();

  const [reports, setReports] = useState<embeddedReport[]>(
    reportIds.map((id) => ({
      loading: true,
      error: false,
      id,
      embedUrl: "",
      embedToken: "",
      pageId: "",
    }))
  );

  const fetchReport = useCallback(
    async (reportId: string) => {
      let errorMessage: string | undefined = undefined;
      setReports((prev) =>
        updateReportData(prev, {
          id: reportId,
          loading: true,
          error: false,
        })
      );
      try {
        const report: reportResponse = await APICall({
          ...getPowerBiEmbedUrl(reportId),
        });

        if (report.resultCode !== 0 || !report.response) {
          errorMessage = report.errorList?.[0]?.errorMessage ?? "Unknown error";
        }
        setReports((prev) =>
          updateReportData(prev, {
            id: reportId,
            loading: false,
            error: !!errorMessage,
            errorMessage: errorMessage,
            embedUrl: !errorMessage ? report.response.embedUrl : "",
            embedToken: !errorMessage ? report.response.embedToken : "",
            pageId: !errorMessage ? report.response.pageId : "",
          })
        );
      } catch (error) {
        errorMessage = error instanceof Error ? error.message : "Unknown error";
        setReports((prev) =>
          updateReportData(prev, {
            id: reportId,
            loading: false,
            error: !!errorMessage,
            errorMessage: errorMessage,
            embedUrl: "",
            embedToken: "",
            pageId: "",
          })
        );
      }
    },
    [APICall]
  );

  const fetchAllReports = useCallback(async () => {
    reports.forEach(async (reportRequest) => {
      fetchReport(reportRequest.id);
    });
  }, [reports, fetchReport]);

  useEffect(() => {
    fetchAllReports();
  }, []);

  return { refreshReport: fetchReport, reports };
};

export default useGetEmbeddedURLs;
